import DashboardLayout from '@/components/DashboardLayout'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Users,
  Plus,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Edit,
  CheckCircle,
  XCircle,
  DollarSign,
  Snowflake,
  Play,
  Clock,
  Hourglass,
  CircleOff,
  Download,
} from 'lucide-react'
import { FilterToolbar } from '@/components/filters/FilterToolbar'
import { PerPageSelect } from '@/components/filters/PerPageSelect'
import { EmptyResults } from '@/components/filters/EmptyResults'
import { chipClass } from '@/components/filters/chip'
import { cn } from '@/lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { applyCpfMask } from '@/components/ui/cpf-input'
import type {
  ActiveChip,
  FilterSection,
  FilterValues,
  QuickFilter,
} from '@/components/filters/types'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useState } from 'react'
import React from 'react'

interface Student {
  id: number
  name: string
  email: string
  /** Só dígitos — a máscara é aplicada na exibição. */
  document?: string | null
}

interface LessonPackage {
  id: number
  name: string
  lessons_count: number
  price: string
  formatted_price: string
}

interface Payment {
  id: number
  status: string
  payment_method: string
  /** Por onde o dinheiro entrou — ver Payment::GATEWAYS. */
  gateway: Gateway
  paid_at?: string
  amount: number
}

type Gateway = 'stripe' | 'asaas' | 'manual'

interface ReceiptTotal {
  total: number
  count: number
}

/** Quanto entrou por origem, só pagamentos pagos. `all` é a soma das três. */
type ReceiptsSummary = Record<Gateway | 'all', ReceiptTotal>

interface Subscription {
  id: number
  purchased_at: string
  lessons_remaining: number
  status: string
  effective_status: string
  total_paid: string
  created_at: string
  discount_percentage: number
  final_price: string
  formatted_final_price: string
  discount_amount: string
  booking_deadline?: string
  expires_at?: string | null
  frozen_at?: string | null
  /** Data-limite real: a mais restritiva entre validade e prazo de marcação. */
  booking_cutoff_date?: string | null
  student: Student
  lesson_package: LessonPackage
  payment?: Payment
}

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Filters {
  status: string
  search: string
  payment_status?: string
  expiring?: string
  purchased_from?: string
  purchased_to?: string
  gateway?: string | null
  lesson_package_id?: string | null
  paid_from?: string
  paid_to?: string
  sort_by: string
  sort_direction: string
}

/** Quantas assinaturas caem em cada chip de status, com a busca aplicada. */
type StatusCounts = Record<string, number>

interface Props {
  subscriptions: Subscription[]
  pagination: Pagination
  filters: Filters
  status_counts: StatusCounts
  /** Só chega para o dono: valores não são delegáveis ao gerente. */
  receipts_summary?: ReceiptsSummary | null
  lesson_packages: { id: number; name: string }[]
}

const STATUS_MAP: Record<
  string,
  {
    label: string
    variant: 'default' | 'secondary' | 'destructive' | 'outline'
  }
> = {
  active: { label: 'Ativa', variant: 'default' },
  completed: { label: 'Concluída', variant: 'secondary' },
  cancelled: { label: 'Cancelada', variant: 'destructive' },
  expired: { label: 'Vencida', variant: 'destructive' },
  paused: { label: 'Congelada', variant: 'outline' },
}

const PAYMENT_STATUS_MAP: Record<
  string,
  {
    label: string
    variant: 'default' | 'secondary' | 'destructive' | 'outline'
    icon: any
  }
> = {
  pending: { label: 'Pendente', variant: 'outline', icon: XCircle },
  paid: { label: 'Pago', variant: 'default', icon: CheckCircle },
  cancelled: { label: 'Cancelado', variant: 'destructive', icon: XCircle },
  refunded: { label: 'Reembolsado', variant: 'secondary', icon: XCircle },
}

// Chips de status. A ordem é a do fluxo real de uma assinatura, e "Congeladas" existe
// aqui porque a tela já tem botão de congelar/descongelar mas não tinha como listar o
// que estava congelado. Os recortes usam o status EFETIVO (mesmo que a linha exibe):
// filtrar por "Ativas" e receber linhas escritas "Vencida" era o comportamento anterior.
const STATUS_CHIPS = [
  { value: 'all', label: 'Todas' },
  { value: 'active', label: 'Ativas' },
  { value: 'paused', label: 'Congeladas' },
  { value: 'expired', label: 'Vencidas' },
  { value: 'completed', label: 'Concluídas' },
  { value: 'cancelled', label: 'Canceladas' },
] as const

const ADVANCED_FILTER_SECTIONS: readonly FilterSection[] = [
  {
    title: 'Pagamento',
    hint: 'Sem pagamento são as assinaturas importadas do NextFit, que entraram sem cobrança registrada',
    fields: [
      {
        key: 'payment_status',
        label: 'Situação',
        options: [
          { value: 'all', label: 'Todos' },
          { value: 'pending', label: 'Pendente' },
          { value: 'paid', label: 'Pago' },
          { value: 'none', label: 'Sem pagamento' },
          { value: 'cancelled', label: 'Cancelado' },
          { value: 'refunded', label: 'Reembolsado' },
        ],
      },
      {
        key: 'gateway',
        label: 'Origem',
        options: [
          { value: 'all', label: 'Todas' },
          { value: 'stripe', label: 'Stripe' },
          { value: 'asaas', label: 'ASAAS' },
          { value: 'manual', label: 'Manual' },
        ],
      },
    ],
  },
  {
    title: 'Vencimento',
    hint: 'Considera o prazo mais apertado entre validade e prazo de marcação',
    fields: [
      {
        key: 'expiring',
        label: 'Vence em',
        options: [
          { value: 'all', label: 'Qualquer prazo' },
          { value: '7', label: '7 dias' },
          { value: '15', label: '15 dias' },
          { value: '30', label: '30 dias' },
        ],
      },
    ],
  },
]

// Fora das seções acima porque não são escolha entre poucas opções fixas: as faixas de
// data, e o pacote, cuja lista vem da escola e não caberia num SegmentedControl.
// O dialog precisa conhecer as chaves para contá-las no badge e no "Limpar tudo".
const DATE_FILTER_KEYS = [
  'lesson_package_id',
  'purchased_from',
  'purchased_to',
  'paid_from',
  'paid_to',
] as const

const GATEWAY_LABELS: Record<Gateway, string> = {
  stripe: 'Stripe',
  asaas: 'ASAAS',
  manual: 'Manual',
}

// Cards do resumo, na ordem em que o dinheiro passou a entrar por cada caminho.
const RECEIPT_CARDS: readonly { key: Gateway | 'all'; label: string }[] = [
  { key: 'all', label: 'Total recebido' },
  { key: 'stripe', label: 'Stripe' },
  { key: 'asaas', label: 'ASAAS' },
  { key: 'manual', label: 'Manual' },
]

const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    value,
  )

/** Par de datas De/Até do dialog de filtros. */
function DateRangeFields({
  title,
  hint,
  fromKey,
  toKey,
  values,
  setValue,
}: {
  title: string
  hint: string
  fromKey: string
  toKey: string
  values: FilterValues
  setValue: (key: string, value: string) => void
}) {
  return (
    <div className="space-y-4 border-t pt-5">
      <div>
        <h4 className="text-sm font-semibold">{title}</h4>
        <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>
      </div>
      <div className="flex flex-wrap gap-4">
        {[
          { key: fromKey, label: 'De' },
          { key: toKey, label: 'Até' },
        ].map(({ key, label }) => (
          <div key={key} className="flex flex-col gap-2">
            <Label
              htmlFor={key}
              className="text-sm font-normal text-muted-foreground"
            >
              {label}
            </Label>
            <Input
              id={key}
              type="date"
              className="w-[170px]"
              value={values[key] || ''}
              onChange={(e) => setValue(key, e.target.value)}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

const ADVANCED_FILTER_KEYS: string[] = [
  ...ADVANCED_FILTER_SECTIONS.flatMap((section) =>
    section.fields.map((field) => field.key),
  ),
  ...DATE_FILTER_KEYS,
]

// Atalhos: os três recortes que motivam abrir esta tela. "Congeladas" ficou de fora de
// propósito — já é um chip de status ali em cima, e repetir o mesmo recorte em dois
// lugares só faz o usuário duvidar se são a mesma coisa.
const QUICK_FILTERS: readonly QuickFilter[] = [
  {
    label: 'Aguardando pagamento',
    icon: Hourglass,
    values: { payment_status: 'pending' },
  },
  { label: 'Vencendo em 15 dias', icon: Clock, values: { expiring: '15' } },
  {
    label: 'Sem pagamento registrado',
    icon: CircleOff,
    values: { payment_status: 'none' },
  },
]

const FILTER_LABELS: Record<
  string,
  { field: string; options: Record<string, string> }
> = Object.fromEntries(
  ADVANCED_FILTER_SECTIONS.flatMap((section) =>
    section.fields.map((field) => [
      field.key,
      {
        field: field.label === 'Situação' ? 'Pagamento' : field.label,
        options: Object.fromEntries(
          field.options.map((option) => [option.value, option.label]),
        ),
      },
    ]),
  ),
)

/**
 * Validade da assinatura: a data-limite e quanto falta para ela.
 *
 * A coluna existe porque o filtro "vencendo em breve" não tinha como ser conferido a
 * olho — a tabela só mostrava a data da compra. Quem está fechando renovação precisa ver
 * o prazo na mesma linha do aluno.
 */
function ValidityCell({
  cutoff,
  status,
}: {
  cutoff?: string | null
  status: string
}) {
  if (!cutoff) {
    return <span className="text-xs text-muted-foreground">Sem prazo</span>
  }

  const date = new Date(`${cutoff}T00:00:00`)
  const days = Math.ceil(
    (date.getTime() - new Date().setHours(0, 0, 0, 0)) / 86_400_000,
  )
  const label = format(date, 'dd/MM/yyyy', { locale: ptBR })

  // Só faz sentido contar os dias de quem ainda está correndo: numa assinatura cancelada
  // ou concluída, "vence em 3 dias" seria um alarme falso.
  if (status !== 'active') {
    return <span className="text-sm text-muted-foreground">{label}</span>
  }

  return (
    <div className="flex flex-col">
      <span className="text-sm">{label}</span>
      <span
        className={
          days <= 7
            ? 'text-xs font-medium text-orange-600'
            : 'text-xs text-muted-foreground'
        }
      >
        {days === 0
          ? 'vence hoje'
          : days === 1
            ? 'vence amanhã'
            : `em ${days} dias`}
      </span>
    </div>
  )
}

// "2026-09-08" -> "08/09/2026". Entrada é o value cru do <input type="date">, sempre ISO.
function formatDateLabel(value: string) {
  const [year, month, day] = value.split('-')
  return day && month && year ? `${day}/${month}/${year}` : value
}

function receiptPeriodLabel(from?: string, to?: string) {
  if (from && to)
    return `recebidos de ${formatDateLabel(from)} a ${formatDateLabel(to)}`
  if (from) return `recebidos a partir de ${formatDateLabel(from)}`
  if (to) return `recebidos até ${formatDateLabel(to)}`
  return 'todo o histórico'
}

const PAYMENT_METHOD_MAP: Record<string, string> = {
  cash: 'Dinheiro',
  credit_card: 'Cartão de Crédito',
  debit_card: 'Cartão de Débito',
  pix: 'PIX',
  bank_transfer: 'Transferência',
}

export default function Index({
  subscriptions,
  pagination,
  filters,
  status_counts,
  receipts_summary,
  lesson_packages,
}: Props) {
  const [confirmingPayment, setConfirmingPayment] = useState<number | null>(
    null,
  )

  const filterValues: FilterValues = Object.fromEntries(
    ADVANCED_FILTER_KEYS.map((key) => [
      key,
      (filters as unknown as Record<string, string | undefined>)[key] || '',
    ]),
  )

  const activeChips: ActiveChip[] = ADVANCED_FILTER_KEYS.flatMap((key) => {
    const value = filterValues[key]
    if (!value) return []

    if (key === 'purchased_from')
      return [{ key, label: `Comprada a partir de ${formatDateLabel(value)}` }]
    if (key === 'purchased_to')
      return [{ key, label: `Comprada até ${formatDateLabel(value)}` }]
    if (key === 'paid_from')
      return [{ key, label: `Recebido a partir de ${formatDateLabel(value)}` }]
    if (key === 'paid_to')
      return [{ key, label: `Recebido até ${formatDateLabel(value)}` }]
    if (key === 'lesson_package_id') {
      const name = lesson_packages.find((p) => String(p.id) === value)?.name
      return [{ key, label: `Pacote: ${name ?? value}` }]
    }

    const labels = FILTER_LABELS[key]
    return [
      { key, label: `${labels.field}: ${labels.options[value] ?? value}` },
    ]
  })

  const hasAnyFilter =
    activeChips.length > 0 ||
    Boolean(filters.search) ||
    filters.status !== 'all'

  const visit = (params: Record<string, unknown>) =>
    router.get(
      '/student_subscriptions',
      { ...filters, page: 1, ...params },
      {
        preserveState: true,
        preserveScroll: true,
      },
    )

  const clearAllFilters = () =>
    visit({
      ...Object.fromEntries(ADVANCED_FILTER_KEYS.map((key) => [key, ''])),
      search: '',
      status: 'all',
    })

  const confirmPayment = async (subscriptionId: number) => {
    setConfirmingPayment(subscriptionId)

    try {
      const response = await fetch(
        `/student_subscriptions/${subscriptionId}/confirm_payment`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token':
              document
                .querySelector('meta[name="csrf-token"]')
                ?.getAttribute('content') || '',
          },
        },
      )

      const data = await response.json()

      if (data.success) {
        router.reload()
      }
    } catch (error) {
      console.error(error)
    } finally {
      setConfirmingPayment(null)
    }
  }

  // PDF dos recebimentos do recorte que está na tela — inclusive a origem escolhida nos
  // cards. Navegação comum, não Inertia: a resposta é um arquivo. Página e ordenação
  // ficam de fora porque o PDF traz o recorte inteiro, sempre do mais recente.
  const exportReceiptsPdf = () => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value && key !== 'sort_by' && key !== 'sort_direction')
        params.set(key, String(value))
    })
    window.location.href = `/student_subscriptions/receipts?${params.toString()}`
  }

  // Uma requisição só, com as duas chaves juntas. Antes eram dois router.get em sequência
  // partindo do mesmo `filters`: o segundo cancelava o primeiro e devolvia sort_by ao
  // valor antigo, então clicar numa coluna nova só invertia a direção da anterior.
  const handleSort = (column: string) => {
    const newDirection =
      filters.sort_by === column && filters.sort_direction === 'asc'
        ? 'desc'
        : 'asc'
    visit({ sort_by: column, sort_direction: newDirection })
  }

  const handlePageChange = (page: number) =>
    router.get(
      '/student_subscriptions',
      { ...filters, page },
      { preserveState: true },
    )

  return (
    <DashboardLayout>
      <div className="p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Assinaturas de Alunos</h1>
              <p className="text-muted-foreground">
                {pagination.total_count}{' '}
                {pagination.total_count === 1 ? 'assinatura' : 'assinaturas'}
              </p>
            </div>
          </div>
          <Button onClick={() => router.visit('/student_subscriptions/new')}>
            <Plus className="w-4 h-4 mr-2" />
            Nova Assinatura
          </Button>
        </div>

        {/* Filtros */}
        <Card className="mb-6">
          <CardContent className="p-6 space-y-4">
            <FilterToolbar
              searchPlaceholder="Buscar por aluno, CPF, e-mail, pacote ou #ID..."
              searchTerm={filters.search || ''}
              onSearch={(term) => visit({ search: term })}
              sections={ADVANCED_FILTER_SECTIONS}
              quickFilters={QUICK_FILTERS}
              values={filterValues}
              onApplyFilters={(values) => visit(values)}
              dialogDescription="A busca por aluno, CPF, pacote ou #ID continua valendo junto com estes filtros."
              extraKeys={DATE_FILTER_KEYS}
              dialogExtras={(values, setValue) => (
                <>
                  <div className="space-y-3 border-t pt-5">
                    <Label
                      htmlFor="lesson_package_id"
                      className="text-sm font-semibold"
                    >
                      Pacote de aulas
                    </Label>
                    <Select
                      value={values.lesson_package_id || 'all'}
                      onValueChange={(value) =>
                        setValue(
                          'lesson_package_id',
                          value === 'all' ? '' : value,
                        )
                      }
                    >
                      <SelectTrigger
                        id="lesson_package_id"
                        className="w-full sm:w-[320px]"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os pacotes</SelectItem>
                        {lesson_packages.map((lessonPackage) => (
                          <SelectItem
                            key={lessonPackage.id}
                            value={String(lessonPackage.id)}
                          >
                            {lessonPackage.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <DateRangeFields
                    title="Período de recebimento"
                    hint="Pela data em que o pagamento foi confirmado. Só considera o que está pago."
                    fromKey="paid_from"
                    toKey="paid_to"
                    values={values}
                    setValue={setValue}
                  />
                  <DateRangeFields
                    title="Período de compra"
                    hint="Deixe um dos lados em branco para não limitar aquela ponta."
                    fromKey="purchased_from"
                    toKey="purchased_to"
                    values={values}
                    setValue={setValue}
                  />
                </>
              )}
              activeChips={activeChips}
              onRemoveFilter={(key) => visit({ [key]: '' })}
              onClearAll={clearAllFilters}
            />

            {/* Status como fila de chips, não select: os números dão o panorama da escola
                sem obrigar a filtrar um por um para descobrir onde está o volume. */}
            <div className="flex flex-wrap gap-2 border-t pt-4">
              {STATUS_CHIPS.map((chip) => {
                const selected = filters.status === chip.value
                const count = status_counts?.[chip.value]
                return (
                  <button
                    key={chip.value}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => visit({ status: chip.value })}
                    className={chipClass(selected)}
                  >
                    {chip.label}
                    {count !== undefined && (
                      <span
                        className={
                          selected ? 'font-semibold' : 'text-muted-foreground'
                        }
                      >
                        {count}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </CardContent>
        </Card>

        {/* Recebido por origem. Os cards valem como filtro: clicar recorta a lista por
            aquela origem, e os outros continuam mostrando o próprio valor (o resumo
            ignora o filtro de origem, como os chips de status ignoram o status). */}
        {receipts_summary && (
          <div className="mb-6">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {RECEIPT_CARDS.map((card) => {
                const selected =
                  card.key === 'all'
                    ? !filters.gateway
                    : filters.gateway === card.key
                const { total, count } = receipts_summary[card.key]
                return (
                  <button
                    key={card.key}
                    type="button"
                    aria-pressed={selected}
                    onClick={() =>
                      visit({
                        gateway: card.key === 'all' || selected ? '' : card.key,
                      })
                    }
                    className={cn(
                      'rounded-lg border bg-card p-4 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                      selected
                        ? 'border-primary bg-primary/10'
                        : 'hover:border-foreground/40',
                    )}
                  >
                    <div className="text-sm text-muted-foreground">
                      {card.label}
                    </div>
                    <div className="mt-1 text-2xl font-bold tabular-nums">
                      {formatBRL(total)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {count} {count === 1 ? 'pagamento' : 'pagamentos'}
                    </div>
                  </button>
                )
              })}
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                Pagamentos confirmados das assinaturas filtradas, valor bruto ·{' '}
                {receiptPeriodLabel(filters.paid_from, filters.paid_to)}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={exportReceiptsPdf}
                disabled={
                  receipts_summary[(filters.gateway as Gateway) || 'all']
                    .count === 0
                }
              >
                <Download className="w-4 h-4 mr-2" />
                {filters.gateway
                  ? `Gerar PDF (${GATEWAY_LABELS[filters.gateway as Gateway]})`
                  : 'Gerar PDF'}
              </Button>
            </div>
          </div>
        )}

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle>Lista de Assinaturas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('id')}
                    >
                      <div className="flex items-center gap-2">
                        ID
                        <ArrowUpDown className="w-4 h-4" />
                      </div>
                    </TableHead>
                    <TableHead>Aluno</TableHead>
                    <TableHead>Pacote</TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('lessons_remaining')}
                    >
                      <div className="flex items-center gap-2">
                        Aulas Restantes
                        <ArrowUpDown className="w-4 h-4" />
                      </div>
                    </TableHead>
                    <TableHead>Desconto</TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center gap-2">
                        Status
                        <ArrowUpDown className="w-4 h-4" />
                      </div>
                    </TableHead>
                    <TableHead>Valor Final</TableHead>
                    <TableHead>Status Pagamento</TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('purchased_at')}
                    >
                      <div className="flex items-center gap-2">
                        Data de Compra
                        <ArrowUpDown className="w-4 h-4" />
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('expires_at')}
                    >
                      <div className="flex items-center gap-2">
                        Validade
                        <ArrowUpDown className="w-4 h-4" />
                      </div>
                    </TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscriptions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <EmptyResults
                          message="Nenhuma assinatura encontrada"
                          term={filters.search}
                          hasFilters={hasAnyFilter}
                          onClear={clearAllFilters}
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    subscriptions.map((subscription) => (
                      <TableRow key={subscription.id}>
                        <TableCell className="font-medium">
                          #{subscription.id}
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {subscription.student.name}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {subscription.student.email}
                            </div>
                            {/* CPF logo abaixo: é por ele que a secretaria confirma que
                                achou a pessoa certa quando há homônimos. */}
                            {subscription.student.document && (
                              <div className="text-xs text-muted-foreground">
                                {applyCpfMask(subscription.student.document)}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {subscription.lesson_package.name}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {subscription.lesson_package.lessons_count} aulas
                              •{' '}
                              {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              }).format(
                                parseFloat(subscription.lesson_package.price),
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              subscription.lessons_remaining > 0
                                ? 'default'
                                : 'secondary'
                            }
                          >
                            {subscription.lessons_remaining}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {subscription.discount_percentage > 0 ? (
                            <div>
                              <Badge
                                variant="outline"
                                className="bg-orange-50 text-orange-700 border-orange-200"
                              >
                                {subscription.discount_percentage}%
                              </Badge>
                              <div className="text-xs text-muted-foreground mt-1">
                                -
                                {new Intl.NumberFormat('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL',
                                }).format(
                                  parseFloat(subscription.discount_amount),
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              -
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              STATUS_MAP[
                                subscription.effective_status ||
                                  subscription.status
                              ]?.variant || 'default'
                            }
                          >
                            {STATUS_MAP[
                              subscription.effective_status ||
                                subscription.status
                            ]?.label ||
                              subscription.effective_status ||
                              subscription.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-semibold text-green-700">
                              {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              }).format(parseFloat(subscription.final_price))}
                            </div>
                            {subscription.discount_percentage > 0 && (
                              <div className="text-xs text-muted-foreground line-through">
                                {new Intl.NumberFormat('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL',
                                }).format(
                                  parseFloat(subscription.lesson_package.price),
                                )}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {subscription.payment ? (
                            <div className="space-y-1">
                              <Badge
                                variant={
                                  PAYMENT_STATUS_MAP[
                                    subscription.payment.status
                                  ]?.variant || 'outline'
                                }
                                className="flex items-center gap-1 w-fit"
                              >
                                {React.createElement(
                                  PAYMENT_STATUS_MAP[
                                    subscription.payment.status
                                  ]?.icon || XCircle,
                                  { className: 'w-3 h-3' },
                                )}
                                {PAYMENT_STATUS_MAP[subscription.payment.status]
                                  ?.label || subscription.payment.status}
                              </Badge>
                              <div className="text-xs text-muted-foreground">
                                {PAYMENT_METHOD_MAP[
                                  subscription.payment.payment_method
                                ] || subscription.payment.payment_method}
                                {' · '}
                                {GATEWAY_LABELS[subscription.payment.gateway]}
                              </div>
                              {subscription.payment.paid_at && (
                                <div className="text-xs text-green-600">
                                  Pago em {subscription.payment.paid_at}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              -
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {format(
                            new Date(subscription.purchased_at),
                            'dd/MM/yyyy',
                            { locale: ptBR },
                          )}
                        </TableCell>
                        <TableCell>
                          <ValidityCell
                            cutoff={subscription.booking_cutoff_date}
                            status={
                              subscription.effective_status ||
                              subscription.status
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TooltipProvider>
                            <div className="flex items-center justify-end gap-1">
                              {/* Botão Editar */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() =>
                                      router.visit(
                                        `/student_subscriptions/${subscription.id}/edit`,
                                      )
                                    }
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Editar assinatura</p>
                                </TooltipContent>
                              </Tooltip>

                              {/* Congelar / Descongelar */}
                              {subscription.status === 'active' && (
                                <AlertDialog>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <AlertDialogTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 text-blue-600 hover:bg-blue-50"
                                        >
                                          <Snowflake className="w-4 h-4" />
                                        </Button>
                                      </AlertDialogTrigger>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Congelar assinatura</p>
                                    </TooltipContent>
                                  </Tooltip>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>
                                        Congelar assinatura?
                                      </AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Enquanto congelada, o prazo de validade
                                        fica pausado e o aluno não consegue
                                        agendar. Quando descongelar, o
                                        vencimento será estendido pelo período
                                        congelado.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>
                                        Cancelar
                                      </AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() =>
                                          router.patch(
                                            `/student_subscriptions/${subscription.id}/freeze`,
                                            {},
                                            {
                                              preserveScroll: true,
                                              onError: (errors) =>
                                                toast.error(
                                                  errors.base ||
                                                    'Não foi possível congelar.',
                                                ),
                                            },
                                          )
                                        }
                                      >
                                        Congelar
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}

                              {subscription.status === 'paused' && (
                                <AlertDialog>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <AlertDialogTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 text-emerald-600 hover:bg-emerald-50"
                                        >
                                          <Play className="w-4 h-4" />
                                        </Button>
                                      </AlertDialogTrigger>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Descongelar assinatura</p>
                                    </TooltipContent>
                                  </Tooltip>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>
                                        Descongelar assinatura?
                                      </AlertDialogTitle>
                                      <AlertDialogDescription>
                                        A assinatura volta a valer a partir de
                                        hoje pelos dias que ainda restavam
                                        quando foi congelada. O aluno poderá
                                        agendar novamente.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>
                                        Cancelar
                                      </AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() =>
                                          router.patch(
                                            `/student_subscriptions/${subscription.id}/unfreeze`,
                                            {},
                                            {
                                              preserveScroll: true,
                                              onSuccess: () => router.reload(),
                                              onError: (errors) =>
                                                toast.error(
                                                  errors.base ||
                                                    'Não foi possível descongelar.',
                                                ),
                                            },
                                          )
                                        }
                                      >
                                        Descongelar
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}

                              {/* Botão Confirmar Pagamento - só aparece se pendente */}
                              {subscription.payment?.status === 'pending' && (
                                <AlertDialog>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <AlertDialogTrigger asChild>
                                        <Button
                                          variant="default"
                                          size="icon"
                                          className="h-8 w-8 bg-green-600 hover:bg-green-700"
                                          disabled={
                                            confirmingPayment ===
                                            subscription.id
                                          }
                                        >
                                          {confirmingPayment ===
                                          subscription.id ? (
                                            <div className="animate-spin">
                                              ⏳
                                            </div>
                                          ) : (
                                            <DollarSign className="w-4 h-4" />
                                          )}
                                        </Button>
                                      </AlertDialogTrigger>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-green-600 text-white">
                                      <p className="font-semibold">
                                        💰 Confirmar Pagamento
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>
                                        Confirmar Pagamento
                                      </AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Tem certeza que deseja confirmar o
                                        pagamento da assinatura de{' '}
                                        <strong>
                                          {subscription.student.name}
                                        </strong>
                                        ?
                                        <br />
                                        <br />
                                        Pacote:{' '}
                                        <strong>
                                          {subscription.lesson_package.name}
                                        </strong>
                                        <br />
                                        Valor:{' '}
                                        <strong>
                                          {new Intl.NumberFormat('pt-BR', {
                                            style: 'currency',
                                            currency: 'BRL',
                                          }).format(
                                            parseFloat(
                                              subscription.final_price,
                                            ),
                                          )}
                                        </strong>
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>
                                        Cancelar
                                      </AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() =>
                                          confirmPayment(subscription.id)
                                        }
                                        className="bg-green-600 hover:bg-green-700"
                                      >
                                        Confirmar Pagamento
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </TooltipProvider>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Paginação */}
            <div className="flex items-center justify-between gap-4 flex-wrap mt-6">
              <PerPageSelect
                value={pagination.per_page}
                onChange={(perPage) => visit({ per_page: perPage })}
              />
              {pagination.total_pages > 1 && (
                <div className="text-sm text-muted-foreground">
                  Mostrando{' '}
                  {(pagination.current_page - 1) * pagination.per_page + 1} a{' '}
                  {Math.min(
                    pagination.current_page * pagination.per_page,
                    pagination.total_count,
                  )}{' '}
                  de {pagination.total_count} assinaturas
                </div>
              )}
              {pagination.total_pages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      handlePageChange(pagination.current_page - 1)
                    }
                    disabled={pagination.current_page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div className="text-sm">
                    Página {pagination.current_page} de {pagination.total_pages}
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      handlePageChange(pagination.current_page + 1)
                    }
                    disabled={
                      pagination.current_page === pagination.total_pages
                    }
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
